import classNames from 'classnames';
import {
  CreateDueRemainderRequest,
  IncidentResultStatus,
  ReminderMode,
  TaskPriority,
  UserRole,
} from 'compliance-api/generated';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProps, FormikProvider } from 'formik';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { RRule } from 'rrule';
import { formatTaskDateTimeWithLocalTime } from '@/modules/common/helpers/taskDateTime.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import DepartmentsSelectPaginated from '@/modules/admin-dashboard/components/DepartmentsSelectPaginated.tsx';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import IncidentsSelectPaginate, {
  EventItemOption,
} from '@/modules/admin-dashboard/components/IncidentsSelectPaginate.tsx';
import ReminderItem from '@/modules/admin-dashboard/components/ReminderItem.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { TaskFormValues } from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import {
  Button,
  CalendarInput,
  CustomSwitch,
  ModalContentWrapper,
  RRuleComponent,
  Select,
  SelectButtonOption,
  SelectButtons,
  SelectPaginateOption,
  Spinner,
  TextInputField,
  TooltipNew,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { fixNameEnum, getDisableTimeBefore } from 'ui/utils';

dayjs.extend(utc);

const isEveryBodyNeedsToBeCompletedOptions: SelectButtonOption<boolean>[] = [
  { label: 'yes', value: true },
  { label: 'no', value: false },
];

export interface TaskModalProps {
  disabledMode?: boolean;
  setDisabledMode?: Dispatch<SetStateAction<boolean>>;
  isUpdating?: boolean;
  isOpened: boolean;
  formik: FormikProps<TaskFormValues>;
  isTaskLoading: boolean;
  handleCloseModal: () => void;
  incident_result_id?: number;
  isTaskDataLoading?: boolean;
  isEveryBodyNeedsToBeCompleted?: boolean;
  defaultForm?: SelectPaginateOption<number> | null;
  defaultIncident?: SelectPaginateOption<{ id: number }> | null;
  clientId?: number;
  occurrenceStartDate?: string;
  occurrenceDueDate?: string;
  firstOccurrenceDate?: string;
}

const getDefaultRemainder = () => ({
  days_before_due: 7,
  frequency: new RRule({ freq: RRule.DAILY, interval: 1, dtstart: new Date() }).toString(),
  is_when_overdue: false,
  mode: ReminderMode.Recurring,
  stop_after: 90,
});

const applyDateWithPreservedTime = (date: Dayjs, currentDate: string, fallbackHour: number) => {
  const current = currentDate ? dayjs(currentDate) : null;

  if (date?.isValid()) {
    return date.toISOString();
  }

  if (current?.isValid()) {
    return date
      .hour(current.hour())
      .minute(current.minute())
      .second(current.second())
      .millisecond(current.millisecond())
      .toISOString();
  }

  if (fallbackHour) {
    return date.hour(fallbackHour).minute(0).second(0).millisecond(0).toISOString();
  }
};

const isPastDate = (date: Dayjs) => date.isBefore(dayjs(), 'day');

export const priorityColors: Record<TaskPriority, string> = {
  [TaskPriority.Normal]: 'bg-green-500',
  [TaskPriority.High]: 'bg-orange-500',
  [TaskPriority.Critical]: 'bg-red-500',
};

export const priorityOptions = Object.values(TaskPriority).map(value => ({
  value: value,
  label: (
    <div className="flex items-center">
      <span className={`inline-block w-2.5 h-2.5 mr-2 rounded-sm ${priorityColors[value]}`} />
      <span>{fixNameEnum(value)}</span>
    </div>
  ),
}));

export const TaskModal = ({
  disabledMode,
  setDisabledMode,
  isUpdating,
  isOpened,
  formik,
  isTaskLoading,
  handleCloseModal,
  isTaskDataLoading = false,
  isEveryBodyNeedsToBeCompleted = false,
  defaultForm = null,
  defaultIncident = null,
  clientId,
  occurrenceStartDate,
  occurrenceDueDate,
  firstOccurrenceDate,
}: TaskModalProps) => {
  const { isMobile } = useIsMobile();
  const isTaskLinkedToIncident = !!formik.values.incident_result_id;
  const [displayDateRange, setDisplayDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const firstOccurrenceDateTime = formatTaskDateTimeWithLocalTime(firstOccurrenceDate);

  useEffect(() => {
    if (!isOpened || !occurrenceStartDate || !occurrenceDueDate) return;

    setDisplayDateRange([dayjs(occurrenceStartDate), dayjs(occurrenceDueDate)]);
  }, [isOpened, occurrenceDueDate, occurrenceStartDate]);

  const handleSelectValues = (options: SelectPaginateOption<number>[], title?: string) => {
    formik.setFieldValue(`preservedFullOptions.${title}`, options);

    formik.setFieldValue(
      title || '',
      options.map(value => value.value),
    );
  };

  const handleSelectForm = (option: SelectPaginateOption<number>, title: string) => {
    formik.setFieldValue(title, option.value);
    formik.setFieldError(title, '');
  };

  const handleSelectIncident = (option: SelectPaginateOption<EventItemOption>) => {
    formik.setFieldValue('incident_result_id', option?.value?.id || null);
    formik.setFieldError('incident_result_id', '');

    if (option?.value) {
      formik.setFieldValue('due_remainders', []);
      formik.setFieldValue('recurrency_rule', '');
    }
  };

  const handleSelectClient = (option: SelectPaginateOption<number> | null) => {
    formik.setFieldValue('preservedFullOptions.client_id', option);
    formik.setFieldValue('client_id', option?.value || null);
  };

  const handleSelectPriority = (option: TaskPriority | null) => {
    formik.setFieldValue('priority', option);
  };

  const handleDeleteReminder = useCallback(
    (index: number) => {
      formik.setFieldValue(
        'due_remainders',
        formik.values.due_remainders?.filter((_: any, i: number) => i !== index),
      );
    },
    [formik.setFieldValue, formik.values.due_remainders],
  );

  const handleChangeReminder = useCallback(
    (index: number, value: Partial<CreateDueRemainderRequest>) => {
      formik.setFieldValue(
        'due_remainders',
        formik.values.due_remainders?.map((reminder: CreateDueRemainderRequest, i: number) =>
          i === index ? { ...reminder, ...value } : reminder,
        ),
      );
    },
    [formik.setFieldValue, formik.values.due_remainders],
  );

  const addRemainder = useCallback(() => {
    const newReminders = formik.values.due_remainders?.length
      ? [...formik.values.due_remainders, getDefaultRemainder()]
      : [
          { ...getDefaultRemainder(), is_when_overdue: true, days_before_due: 0 },
          getDefaultRemainder(),
        ];

    formik.setFieldValue('due_remainders', newReminders);
  }, [formik.setFieldValue, formik.values.due_remainders]);

  const remaindersHandler = (checked: boolean) => {
    if (!checked) {
      formik.setFieldValue('due_remainders', []);
    } else {
      const rule = new RRule({ dtstart: new Date() });
      delete rule.origOptions.freq; // Remove default frequency to represent "Never"

      formik.setFieldValue('due_remainders', [
        {
          is_when_overdue: true,
          days_before_due: 0,
          mode: ReminderMode.Recurring,
          frequency: rule.toString(),
          stop_after: null,
        },
      ]);
    }
  };

  const [selectedOption, setSelectedOption] = useState<SelectButtonOption<boolean>>(
    isEveryBodyNeedsToBeCompletedOptions[1],
  );

  const handleChange = (value: boolean) => {
    const selected = isEveryBodyNeedsToBeCompletedOptions.find(option => option.value == value);
    if (selected) {
      setSelectedOption(selected);
    }
    formik.setFieldValue('is_every_employee_needs_to_complete', value);
  };

  useEffect(() => {
    handleChange(isEveryBodyNeedsToBeCompleted);
  }, [isEveryBodyNeedsToBeCompleted]);

  const handleStartDate = useCallback(
    (date: Dayjs) => {
      formik.setFieldValue(
        'start_date',
        applyDateWithPreservedTime(date, formik.values.start_date, 9),
      );

      formik.setFieldError('start_date', '');
    },
    [formik],
  );

  const handleFirstDueDate = useCallback(
    (date: Dayjs) => {
      formik.setFieldValue(
        'first_due_date',
        applyDateWithPreservedTime(date, formik.values.first_due_date, 21),
      );

      formik.setFieldError('first_due_date', '');
    },
    [formik],
  );

  const handleChangeDateRange = useCallback(
    (range: [Dayjs | null, Dayjs | null] | null) => {
      if (!range || !range[0] || !range[1]) return;
      const [start, end] = range;
      if (occurrenceStartDate && occurrenceDueDate) {
        setDisplayDateRange([start, end]);
      }
      formik.setFieldError('start_date', '');
      formik.setFieldError('first_due_date', '');
      handleStartDate(start);
      handleFirstDueDate(end);
      if (isPastDate(start)) {
        formik.setFieldError('start_date', 'Start date cannot be in the past');
        return;
      }

      if (isPastDate(end)) {
        formik.setFieldError('first_due_date', 'Due date cannot be in the past');
        return;
      }
    },
    [handleFirstDueDate, handleStartDate, occurrenceDueDate, occurrenceStartDate],
  );

  const RRuleHandler = (rrule: string) => {
    formik.setFieldValue('recurrency_rule', rrule);
  };

  const switchRRule = (checked: boolean) => {
    const rrule = checked
      ? new RRule({
          freq: 1,
          interval: 1,
          bymonthday: new Date(formik.values.start_date)?.getDate() || 1,
        }).toString()
      : '';

    formik.setFieldValue('recurrency_rule', rrule);
  };

  useEffect(() => {
    // sync rrule & task due date
    if (!formik.values.recurrency_rule || !formik.values.start_date) return;
    const { origOptions } = RRule.fromString(formik.values.recurrency_rule);

    formik.setFieldValue(
      'recurrency_rule',
      new RRule({
        ...origOptions,
        dtstart: dayjs.utc(formik.values.start_date).toDate(),
      }).toString(),
    );
  }, [formik.values.start_date, formik.values.recurrency_rule]); // eslint-disable-line

  useEffect(() => {
    if (
      !formik.values.first_due_date ||
      !formik.values.due_remainders?.length ||
      !formik.values.start_date
    )
      return;
    const dueDate = dayjs.utc(formik.values.first_due_date);
    const startDate = dayjs.utc(formik.values.start_date);

    // Compute new reminders with correct rrules
    const newReminders = formik.values.due_remainders?.map(reminder => {
      if ((reminder.mode ?? ReminderMode.Recurring) === ReminderMode.FixedInterval) {
        return reminder;
      }

      if (!reminder.frequency) return reminder;

      const rRule = RRule.fromString(reminder.frequency);
      const options = rRule.origOptions;

      let newOptions;
      if (reminder.is_when_overdue) {
        // For overdue reminders: dtstart = due date + 1 day, no until date
        newOptions = {
          ...options,
          dtstart: dueDate.add(1, 'day').toDate(),
          until: undefined,
        };
      } else {
        // For before-due reminders: dtstart = max(start_date, due date - days_before_due), until = due date
        const reminderStartDate = dueDate.subtract(
          reminder.days_before_due ? +reminder.days_before_due : 0,
          'day',
        );
        const clampedStartDate = reminderStartDate.isBefore(startDate)
          ? startDate
          : reminderStartDate;
        newOptions = {
          ...options,
          dtstart: clampedStartDate.toDate(),
          until: dueDate.toDate(),
        };
      }

      const frequency = new RRule(newOptions).toString();

      return { ...reminder, frequency };
    });

    // Only update if reminders actually changed (prevents infinite loop)
    const remindersChanged = newReminders?.some(
      (rem, idx) => rem.frequency !== formik.values.due_remainders?.[idx].frequency,
    );

    if (remindersChanged) {
      formik.setFieldValue('due_remainders', newReminders);
    }
  }, [formik.values.first_due_date, formik.values.due_remainders, formik.values.start_date]);

  useEffect(() => {
    const reminders = formik.values.due_remainders;
    if (!reminders || !reminders.length) return;

    const needsUpdate = reminders.some(
      rem => !rem.is_when_overdue && rem.stop_after !== 0 && rem.stop_after !== null,
    );

    if (needsUpdate) {
      const newReminders = reminders.map(rem => {
        if (!rem.is_when_overdue && rem.stop_after !== 0 && rem.stop_after !== null) {
          return { ...rem, stop_after: null };
        }
        return rem;
      });
      formik.setFieldValue('due_remainders', newReminders, false);
    }
  }, [formik.values.due_remainders, formik.setFieldValue]);

  useEffect(() => {
    if (isOpened && !isTaskDataLoading && isUpdating && !!formik.values.due_remainders?.length) {
      const overdueExists = formik.values.due_remainders?.some(rem => rem.is_when_overdue);

      if (!overdueExists) {
        const rule = new RRule({ dtstart: new Date() });
        delete rule.origOptions.freq; // Remove default frequency to represent "Never"

        const defaultOverdue = {
          is_when_overdue: true,
          days_before_due: 0,
          mode: ReminderMode.Recurring,
          stop_after: null,
          frequency: rule.toString(),
        };

        formik.setFieldValue(
          'due_remainders',
          [defaultOverdue, ...(formik.values.due_remainders || [])],
          false,
        );
      }
    }
  }, [isOpened, isTaskDataLoading, isUpdating, formik.values.due_remainders, formik.setFieldValue]);

  const formDateRange = useMemo<[Dayjs, Dayjs] | null>(() => {
    return formik.values.start_date && formik.values.first_due_date
      ? [dayjs(formik.values.start_date), dayjs(formik.values.first_due_date)]
      : null;
  }, [formik.values.first_due_date, formik.values.start_date]);

  const dateRanges = displayDateRange || formDateRange;

  const hasBeforeDueReminder = formik.values.due_remainders?.some(rem => !rem.is_when_overdue);

  return (
    <ModalContentWrapper
      title={isUpdating ? `Task ${formik.values.name}` : 'Add new task'}
      open={isOpened}
      onClose={handleCloseModal}
      classes={{ dialogPanel: '!max-w-xl' }}
    >
      <FormikProvider value={formik}>
        {isTaskDataLoading ? (
          <div className="flex justify-center items-center h-60 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        ) : (
          <form className="w-full flex flex-col gap-8" onSubmit={e => e.preventDefault()}>
            <TextInputField
              name="name"
              label="Task name"
              disabled={disabledMode}
              placeholder="Enter task name"
            />

            <CalendarInput
              range
              size="small"
              rangeShowTime
              error={formik.errors.start_date || formik.errors.first_due_date}
              showErrorExample={!isUpdating}
              allowClear={false}
              disabled={disabledMode}
              format="DD-MM-YYYY, HH:mm"
              label="START DATE - DUE DATE"
              rangeValue={dateRanges}
              disabledTime={getDisableTimeBefore}
              rangeOnChange={handleChangeDateRange}
              // popupStyle={{  }} //Deprecate
              styles={{
                popup: {
                  root: {
                    marginLeft: isMobile ? '12%' : '0',
                  },
                },
              }}
            />
            {firstOccurrenceDate && (
              <div className="-mt-5 text-xs text-[#6B6B6B]">
                First occurrence: {firstOccurrenceDateTime.taskDateTime}
                {firstOccurrenceDateTime.localDateTime &&
                  ` (local time: ${firstOccurrenceDateTime.localDateTime})`}
              </div>
            )}
            <Select
              disabled={disabledMode}
              value={formik.values.priority}
              onChange={handleSelectPriority}
              name="priority"
              autoHeight={false}
              options={priorityOptions}
              label="Priority"
            />
            {!isTaskLinkedToIncident && (
              <div className="p-4 border rounded-lg">
                <TooltipNew
                  text="First you have to choose due date"
                  className="w-fit"
                  disabled={!!formik.values.first_due_date}
                >
                  <CustomSwitch
                    checked={!!formik.values.recurrency_rule}
                    onChange={switchRRule}
                    disabled={!formik.values.first_due_date || disabledMode}
                    label="Repeat"
                  />
                </TooltipNew>

                {!!formik.values.recurrency_rule && (
                  <div className="mt-6">
                    {disabledMode ? (
                      RRule.fromString(formik.values.recurrency_rule).toText()
                    ) : (
                      <RRuleComponent
                        value={formik.values.recurrency_rule}
                        minEndDate={dayjs(formik.values.first_due_date)}
                        onChange={RRuleHandler}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <DepartmentsSelectPaginated
              enableCreation
              value={formik.values.preservedFullOptions.department_ids}
              isMulti={true}
              disabled={disabledMode}
              formFieldTitle="department_ids"
              label="Assignee departments"
              handleSelectDepartments={handleSelectValues}
            />
            <UsersSelectPaginated
              enableCreation
              value={formik.values.preservedFullOptions.user_ids}
              isMulti={true}
              disabled={disabledMode}
              onChange={handleSelectValues}
              label="Assignee users"
              formFieldTitle="user_ids"
            />
            <UsersSelectPaginated
              enableCreation
              value={formik.values.preservedFullOptions.users_completed_subs}
              isMulti={true}
              disabled={disabledMode}
              onChange={handleSelectValues}
              label="Notified admins when it gets complete"
              formFieldTitle="users_completed_subs"
              user_role={UserRole.Admin}
            />
            <UsersSelectPaginated
              enableCreation
              value={formik.values.preservedFullOptions.users_overdue_subs}
              isMulti={true}
              disabled={disabledMode}
              onChange={handleSelectValues}
              label="Notified users when it gets overdue"
              formFieldTitle="users_overdue_subs"
            />
            <SelectButtons
              disabled={disabledMode}
              options={isEveryBodyNeedsToBeCompletedOptions}
              onChange={handleChange}
              label="Does every user need to complete"
              selected={selectedOption}
            />
            <div className={clientId ? 'hidden' : ''}>
              <ClientsSelectPaginated
                isMulti={false}
                value={formik.values.preservedFullOptions.client_id}
                disabled={disabledMode}
                formFieldTitle="client_id"
                onChange={handleSelectClient}
                isClearable={true}
              />
            </div>
            <FormSelectPaginate
              isMulti={false}
              label="Form"
              defaultValue={defaultForm || undefined}
              error={formik.errors.form_id as string}
              disabled={disabledMode}
              formFieldTitle="form_id"
              onChange={handleSelectForm}
            />
            <IncidentsSelectPaginate
              filterByStatus={IncidentResultStatus.Open}
              disabled={disabledMode}
              label="Event"
              isMulti={false}
              defaultValue={defaultIncident || undefined}
              onChange={handleSelectIncident}
            />
            <TextInputField
              textarea
              disabled={disabledMode}
              className="pb-0"
              name="description"
              label="Task description"
              placeholder="Enter task description"
            />
            {!isTaskLinkedToIncident && (
              <div className="flex justify-between grid-cols-8">
                <CustomSwitch
                  disabled={disabledMode}
                  className="col-span-6"
                  defaultChecked={false}
                  checked={!!formik.values.due_remainders?.length}
                  onChange={remaindersHandler}
                  label="Due reminders"
                />
                <div className="flex gap-4">
                  {!hasBeforeDueReminder && (
                    <button
                      type="button"
                      disabled={disabledMode}
                      onClick={addRemainder}
                      className={classNames('text-blue-700', {
                        'cursor-pointer': !disabledMode,
                        'text-gray-500': disabledMode,
                      })}
                    >
                      Add before due reminder
                    </button>
                  )}
                </div>
              </div>
            )}

            {!isTaskLinkedToIncident && !!formik.values.due_remainders?.length && (
              <div className="flex flex-col-reverse gap-4">
                {formik.values.due_remainders?.map((remainder, index) => (
                  <ReminderItem
                    key={index}
                    index={index}
                    disabled={disabledMode}
                    onChange={handleChangeReminder}
                    onDelete={handleDeleteReminder}
                    remainder={remainder}
                    errors={
                      formik?.errors?.due_remainders?.[index] as unknown as Record<
                        keyof CreateDueRemainderRequest,
                        string
                      >
                    }
                  />
                ))}
              </div>
            )}

            {disabledMode && setDisabledMode ? (
              <Button
                type="button"
                disabled={isTaskLoading}
                loading={isTaskLoading}
                onClick={setDisabledMode.bind(null, false)}
              >
                Edit task
              </Button>
            ) : (
              <Button disabled={isTaskLoading} loading={isTaskLoading} onClick={formik.submitForm}>
                {isUpdating ? 'Update task' : 'Create task'}
              </Button>
            )}
          </form>
        )}
      </FormikProvider>
    </ModalContentWrapper>
  );
};
